import 'babel-polyfill' // IE
import 'debug.addIndicators' // Debug tool for scrollmagic

// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
// CSS
import 'scss/main.scss'

import Vue from 'vue'
// Vendor JS
import axios from 'axios'
import FastClick from 'fastclick'
import Modernizr from 'modernizr'
import momentTz from 'moment-timezone'
import ScrollMagic from 'scrollmagic'
import Vue2TouchEvents from 'vue2-touch-events'
import VueCurrencyFilter from 'vue-currency-filter'
import VueProgressBar from 'vue-progressbar'
import createApp from '@shopify/app-bridge'
import { Redirect } from '@shopify/app-bridge/actions'
import Moment from 'moment'
import { extendMoment } from 'moment-range'

import App from './App'
import router from '@/router'
import store from '@/store'

import { getAccessToken, getShopDomain, setShopDomain, getShopSettings, setSetupIntegration } from '@/utils/auth.js'
import { globals } from '@/mixins/globals.js'
import { validateShareLink } from '@/utils/share.js'

const moment = extendMoment(Moment)

Vue.config.productionTip = false

axios.defaults.baseURL = process.env.API_URL
axios.defaults.headers.common['Authorization'] = `Bearer ${getAccessToken()}`
axios.defaults.headers.common['Accept'] = 'application/json'

// Catch 401s
// axios.interceptors.response.use(function (response) {
//   return response
// }, function (error) {
//   console.log(error.response.status)
//   if (error.response.status === 401) {
//     window.location.href = '/login'
//   } else {
//     return Promise.reject(error)
//   }
// })

// Progress Bar
const options = {
  color: '#21ce99',
  failedColor: '#f1243b',
  thickness: '3px',
  transition: {
    speed: '0.2s',
    opacity: '0.6s',
    termination: 300
  },
  autoRevert: true,
  location: 'top',
  inverse: false
}

Vue.use(VueProgressBar, options)

// Global Methods
Vue.mixin(globals)

// Global ScrollMagic
Vue.prototype.$ScrollMagic = ScrollMagic
Vue.prototype.$ScrollMagicEvent = new Vue({
  data () {
    return {
      controller: null,
      scenes: {}
    }
  },
  created () {
    const vm = this
    vm.$on('addScene', (name, scenes) => {
      Vue.nextTick(() => {
        if (vm.controller === null) {
          vm.controller = new vm.$ScrollMagic.Controller()
        }
        vm.scenes[name] = scenes
        vm.scenes[name].addTo(vm.controller)
      })
    })
    vm.$on('destroy', () => {
      if (vm.controller !== null) {
        vm.controller.destroy(true)
        vm.controller = null
      }
    })
  }
})

Vue.prototype.$shop = getShopSettings()
Vue.prototype.$domain = getShopDomain()
Vue.prototype.$currency = Vue.prototype.$shop ? Vue.prototype.$shop.currency_sign : '$'
Vue.prototype.$timezone = Vue.prototype.$shop ? Vue.prototype.$shop.timezone : null
Vue.prototype.$type = {
  component: 'component',
  layout: 'layout',
  page: 'page'
}
Vue.prototype.$moreLinks = [
  {
    text: 'Settings',
    url: '/settings'
  },
  {
    target: '_blank',
    text: 'Leave a Review',
    url: 'https://apps.shopify.com/altview'
  },
  {
    text: 'Help',
    url: '/help'
  }
]

momentTz.tz.setDefault(Vue.prototype.$timezone)

// Currency filter
Vue.use(VueCurrencyFilter, {
  symbol: Vue.prototype.$currency,
  thousandsSeparator: ',',
  fractionCount: 2,
  fractionSeparator: '.',
  symbolPosition: 'front',
  symbolSpacing: false
})

// Pluralize filter
Vue.filter('pluralize', function (value, count) {
  if (!value) return ''
  if (count === 1) {
    return value
  }
  return `${value}s`
})

// Touch events
Vue.use(Vue2TouchEvents)

// check if route is from share link
router.beforeEach((to, from, next) => {
  const hasShareId = !!to.query.share
  const hasDomain = !!to.query.domain
  const hasDetail = !!to.query.detail

  if (hasShareId && hasDomain && hasDetail) {
    const shareId = to.query.share
    const domain = to.query.domain
    const detail = !!parseInt(to.query.detail)

    setShopDomain(domain)
    Vue.prototype.$domain = domain

    validateShareLink((status, data, response) => {
      if (status === 410) {
        // expired
        next({ name: 'ShareLinkExpired' })
      } else if (status === 404) {
        next({ name: 'ShareLinkInvalid' })
      } else if (status === 200) {
        // valid
        // Refresh the page after it is expired so we kick them out immediately
        if (response.data.valid_for > 0) {
          setTimeout(() => {
            // console.log('Reloading due to share timeout at', response.data.valid_until)
            location.reload()
          }, (response.data.valid_for + 5) * 1000)
          // console.log('Setting timeout for', response.data.valid_for + 5, 'Seconds from now', response.data.valid_for, response.data.valid_until)
        }
        const {
          filterDateRange,
          filtersActiveProductDetailPage,
          filtersActiveProductsPage,
          selectedDateRange
        } = data

        axios.defaults.headers.common['X-Share-Key'] = shareId
        axios.defaults.headers.common['X-Share-Shop'] = domain

        // console.log('data from main.js')
        // console.dir(data)

        // set filters using data from share payload here
        store.commit('user/viewingViaShareLinkAssign', true)
        store.commit('user/shareLinkIsProductDetailPageAssign', detail)
        store.commit('ui/filtersActiveProductDetailPageAssign', filtersActiveProductDetailPage)
        store.commit('ui/filtersActiveProductsPageAssign', filtersActiveProductsPage)
        store.commit('ui/filterDateRangeAssign', moment.range(
          filterDateRange.start,
          filterDateRange.end
        ))
        store.commit('ui/selectedDateRangeAssign', selectedDateRange)

        // keep moving
        next()
      }
    }, () => {
      // not valid
      next({ name: 'ShareLinkInvalid' })
    }, shareId, domain)
  } else {
    if (to.query.setup) {
      setSetupIntegration(to.query.setup)
    }

    // Send to Shopify Admin
    if (!process.env.DEFAULT_SHOP_DOMAIN) {
      if (window.top === window.self && !process.env.AUTH_USERS && getShopDomain()) {
        const domain = getShopDomain()

        axios({
          url: `platforms/shopify/authorize/${domain}`
        })
        .then((response) => {
          const app = createApp({
            apiKey: process.env.APP_KEY,
            shopOrigin: domain,
            forceRedirect: true
          })

          Redirect.create(app).dispatch(Redirect.Action.ADMIN_PATH, response.data.authorization_url)
        })
      }
    }

    next()
  }
})

// check if trying to view a collection from a Shopify admin link
// example: /collection/?id=
router.beforeEach((to, from, next) => {
  const hasId = !!to.query.id

  if (!to.redirectedFrom || !hasId) {
    next()
    return
  }

  const isCollection = to.redirectedFrom.toLowerCase().indexOf('collection') !== -1

  if (isCollection) {
    next({ name: 'Product Collection', params: { id: to.query.id } })
  } else {
    next()
  }
})

/* eslint-disable no-new */
new Vue({
  el: '#app',
  router,
  store,
  template: '<App/>',
  components: { App },
  methods: {
    fastclick () { // Fix 300ms IOS click delays
      FastClick.attach(document.body)
    },
    firefoxTest () {
      Modernizr.addTest('firefox', () => {
        return navigator.userAgent.match(/firefox/i)
      })
      Vue.prototype.$firefox = document.querySelector('html').classList.contains('firefox')
    },
    iosTest () { // Test for IOS
      Modernizr.addTest('ios', () => {
        return navigator.userAgent.match(/(iPad|iPhone|iPod)/g)
      })
      Vue.prototype.$ios = document.querySelector('html').classList.contains('ios')
    },
    userAgent () { // Get user agent info and set as html data-attributes
      let doc = document.documentElement
      doc.setAttribute('data-useragent', navigator.userAgent)
      doc.setAttribute('data-platform', navigator.platform)
    },
    checkShopDomain () {  // Check if shop domain passed in query string
      if (this.$route.query.shop && this.$route.query.shop !== this.$domain) {
        setShopDomain(this.$route.query.shop)
        this.$domain = this.$route.query.shop
        window.location.href = window.location.pathname
      }
    },
    checkShopActive () {
      this.loadUser().then(() => {
        this.checkShopDomain()

        // Send inactive shops to authorization process
        if (this.$shop && !this.$shop.active) {
          this.loadAuthUrl().then((url) => {
            if (url) {
              window.location.href = url
            }
          })
        }
      })
    }
  },
  mounted () { // Document ready
    this.userAgent()
    this.fastclick()
  },
  created () {
    if (this.$route.name !== 'Welcome' && this.$shop && this.$shop.active) {
      if (!this.$shop.initial_import) {
        this.$router.push({name: 'Welcome'})
      }
    }

    this.checkShopActive()
    this.iosTest()
    this.firefoxTest()
  }
})



// WEBPACK FOOTER //
// ./src/main.js